import React, { FC, ReactNode, useContext, useState } from "react";

import { UppAuthContext, PostBillAPIContext } from "@app/provider";
import { logMsg } from "@app/utils/logMsg";
import {
  PaymentHistoryItem,
  PaymentPlansResponseData,
  PaymentsHistoryResponseData,
  StatementItem,
  StatementsResponseData,
} from "../models";
import { formatLocalDate } from "@app/utils/convertDate";
import { PaymentPlanCardItem } from "../models/apiControllerModel";

export enum SORT_BY {
  NEWEST = "newest",
  OLDEST = "oldest",
}

export interface PostbillHistoryController {
  isLoading: boolean;
  testCards:
    | StatementItem[]
    | PaymentHistoryItem[]
    | PaymentPlanCardItem[]
    | undefined;
  setTestCards: (
    testCards: StatementItem[] | PaymentHistoryItem[] | PaymentPlanCardItem[]
  ) => void;
  sortBy: SORT_BY;
  pageNum: number;
  currentPage: number;
  loadStatementsHistory: (
    from?: Date,
    to?: Date,
    page?: number
  ) => Promise<void>;
  loadStatementsPage: (page?: number, from?: Date, to?: Date) => Promise<void>;
  toggleStatementsSort: (from?: Date, to?: Date) => void;
  loadPaymentsHistory: (from?: Date, to?: Date, page?: number) => Promise<void>;
  loadPaymentsPage: (page?: number, from?: Date, to?: Date) => Promise<void>;
  togglePaymentsSort: (from?: Date, to?: Date) => void;
  loadPaymentPlansHistory: (
    from?: Date,
    to?: Date,
    page?: number
  ) => Promise<void>;
  loadPaymentPlansPage: (
    page?: number,
    from?: Date,
    to?: Date
  ) => Promise<void>;
  togglePaymentPlansSort: (from?: Date, to?: Date) => void;
  reset: () => void;
}
export const Context = React.createContext<PostbillHistoryController>({
  isLoading: false,
  testCards: [],
  setTestCards: () => undefined,
  sortBy: SORT_BY.NEWEST,
  pageNum: 0,
  currentPage: 0,
  loadStatementsHistory: async () => Promise.reject(),
  loadStatementsPage: async () => Promise.reject(),
  toggleStatementsSort: () => undefined,
  loadPaymentsHistory: async () => Promise.reject(),
  loadPaymentsPage: async () => Promise.reject(),
  togglePaymentsSort: () => undefined,
  loadPaymentPlansHistory: async () => Promise.reject(),
  loadPaymentPlansPage: async () => Promise.reject(),
  togglePaymentPlansSort: () => undefined,
  reset: () => undefined,
});

Context.displayName = "PostBillHistoryContext";

export { Context as PostBillHistoryContext };

interface PostBillHistoryProviderProps {
  children: ReactNode;
}

const PostBillHistoryProvider: FC<PostBillHistoryProviderProps> = ({
  children,
}) => {
  const { getStatements, getPaymentsHistory, getPaymentPlans } =
    useContext(PostBillAPIContext);
  const { profile: uppProfile } = useContext(UppAuthContext);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageNum, setPageNum] = useState(0);
  const [sortBy, setSortBy] = useState<SORT_BY>(SORT_BY.NEWEST);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [testCards, setTestCards] = useState<
    StatementItem[] | PaymentHistoryItem[] | PaymentPlanCardItem[]
  >([]);
  const perPage = 5;

  const getStatementsItems = async (
    page?: number,
    from?: Date,
    to?: Date,
    sort = sortBy
  ) => {
    setIsLoading(true);
    try {
      const response = await getStatements({
        pageSize: perPage,
        pageNumber: page ?? 0,
        startDate: formatLocalDate(from),
        endDate: formatLocalDate(to),
        sortType: sort,
      });
      return response?.data;
    } catch {
      logMsg("error", "postbill.history", "Error fetching statements");
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getPaymentsHistoryItems = async (
    page?: number,
    from?: Date,
    to?: Date,
    sort = sortBy
  ) => {
    setIsLoading(true);
    try {
      const response = await getPaymentsHistory({
        pageSize: perPage,
        pageNumber: page ?? 0,
        startDate: formatLocalDate(from),
        endDate: formatLocalDate(to),
        sortType: sort,
      });
      return response?.data;
    } catch {
      logMsg("error", "postbill.history", "Error fetching statements");
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getPaymentPlansItems = async (
    page?: number,
    from?: Date,
    to?: Date,
    sort = sortBy
  ) => {
    setIsLoading(true);
    try {
      const response = await getPaymentPlans({
        startDate: from?.toISOString().substring(0, 10),
        endDate: to?.toISOString().substring(0, 10),
        sort,
        page: page?.toString() ?? "0",
        limit: perPage.toString(),
      });
      return response?.data;
    } catch {
      logMsg("error", "postbill.history", "Error fetching payment plans");
    } finally {
      setIsLoading(false);
    }
  };

  const reset = () => {
    setCurrentPage(0);
    setPageNum(0);
    setTestCards([]);
  };

  const toggleStatementsSort = (from?: Date, to?: Date) => {
    const nextSort =
      sortBy === SORT_BY.NEWEST ? SORT_BY.OLDEST : SORT_BY.NEWEST;
    setSortBy(nextSort);
    reset();
    loadStatementsHistory(from, to, 0, nextSort);
  };

  const loadStatementsHistory = async (
    from?: Date,
    to?: Date,
    page?: number,
    sort = sortBy
  ) => {
    if (page !== undefined) {
      setCurrentPage(page);
    }

    const statements = (await getStatementsItems(
      page,
      from,
      to,
      sort
    )) as StatementsResponseData;

    const total = statements?.totalCount as number;
    const pageCount = Math.ceil(total / perPage);

    setPageNum(pageCount);
    setTestCards(statements?.items);
  };

  const loadStatementsPage = async (page?: number, from?: Date, to?: Date) => {
    const nextPage = page ?? currentPage + 1;
    if (nextPage >= pageNum) return;
    return loadStatementsHistory(from, to, nextPage, sortBy);
  };

  const togglePaymentsSort = (from?: Date, to?: Date) => {
    const nextSort =
      sortBy === SORT_BY.NEWEST ? SORT_BY.OLDEST : SORT_BY.NEWEST;
    setSortBy(nextSort);
    reset();
    loadPaymentsHistory(from, to, 0, nextSort);
  };

  const loadPaymentsHistory = async (
    from?: Date,
    to?: Date,
    page?: number,
    sort = sortBy
  ) => {
    if (page !== undefined) {
      setCurrentPage(page);
    }

    const payments = (await getPaymentsHistoryItems(
      page,
      from,
      to,
      sort
    )) as PaymentsHistoryResponseData;

    const total = payments?.totalCount as number;
    const pageCount = Math.ceil(total / perPage);

    setPageNum(pageCount);
    setTestCards(payments?.items);
  };

  const loadPaymentsPage = async (page?: number, from?: Date, to?: Date) => {
    const nextPage = page ?? currentPage + 1;
    if (nextPage >= pageNum) return;
    return loadPaymentsHistory(from, to, nextPage, sortBy);
  };

  const loadPaymentPlansHistory = async (
    from?: Date,
    to?: Date,
    page?: number,
    sort = sortBy
  ) => {
    if (page !== undefined) {
      setCurrentPage(page);
    }
    const paymentPlans = (await getPaymentPlansItems(
      page,
      from,
      to,
      sort
    )) as PaymentPlansResponseData;

    const total = paymentPlans?.totalCount;
    const pageCount = Math.ceil(total / perPage);

    setPageNum(pageCount);
    setTestCards(paymentPlans?.items);
  };

  const loadPaymentPlansPage = async (
    page?: number,
    from?: Date,
    to?: Date
  ) => {
    const nextPage = page ?? currentPage + 1;
    if (nextPage >= pageNum) return;
    return loadPaymentPlansHistory(from, to, nextPage, sortBy);
  };

  const togglePaymentPlansSort = (from?: Date, to?: Date) => {
    const nextSort =
      sortBy === SORT_BY.NEWEST ? SORT_BY.OLDEST : SORT_BY.NEWEST;
    setSortBy(nextSort);
    reset();
    loadPaymentPlansHistory(from, to, 0, nextSort);
  };

  return (
    <Context.Provider
      value={{
        isLoading,
        sortBy,
        pageNum,
        currentPage,
        testCards,
        setTestCards,
        loadStatementsPage,
        loadStatementsHistory,
        toggleStatementsSort,
        loadPaymentsPage,
        loadPaymentsHistory,
        togglePaymentsSort,
        loadPaymentPlansHistory,
        loadPaymentPlansPage,
        togglePaymentPlansSort,
        reset,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default PostBillHistoryProvider;
